import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import { BN, web3 } from "@project-serum/anchor";
import {
  deserializeBorsh,
  getAllGovernances,
  GOVERNANCE_SCHEMA,
  ProposalTransaction,
} from "@solana/spl-governance";
import {
  AccountLayout,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MintLayout,
  NATIVE_MINT,
  Token,
  TOKEN_PROGRAM_ID,
  u64,
} from "@solana/spl-token";
import { AnchorWallet, WalletContextState } from "@solana/wallet-adapter-react";
import {
  AccountInfo,
  Keypair,
  LAMPORTS_PER_SOL,
  ParsedAccountData,
  PublicKey,
  RpcResponseAndContext,
  sendAndConfirmRawTransaction,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import dayjs from "dayjs";
import { divide, pow } from "mathjs";
import { io } from "socket.io-client";
import { getNftDataFromMagicEden } from "../../../api/magic-eden.api";
import { getNftDataFromSolsea } from "../../../api/solsea.api";
import {
  CHAIN_ID,
  EMPTY_STRING,
  MISSING_TOKEN_DETAILS,
  TREASURY_INDEX,
  UNQ_PER_AMOUNT,
  USDC_ADDRESS,
} from "../../../common/constants/common.constants";
import { RPC_ERROR } from "../../../common/constants/network.constants";
import { GovernanceConfigurationType } from "../../../common/enums/clubs-wizard.enum";
import {
  ClubAction,
  ClubActionSolana,
  TradedRightType,
  UserRole,
} from "../../../common/enums/common.enums";
import {
  OfferStatus,
  OfferTypeSolana,
} from "../../../common/enums/proposals.enums";
import {
  MonkeAdminPermissions,
  MonkeAdminStatus,
} from "../../../common/enums/whitelisting.enums";
import {
  ICreateClubWizardForm,
  ICreateFundraiseForm,
} from "../../../common/interfaces/clubs-wizard.interface";
import {
  IClubBasicInfo,
  IClubTreasury,
  IDepositRecord,
  IFundraise,
  IMembersRights,
  IReservedRights,
  ISellPermissionConfig,
  ISellPermissionConfigSolana,
  ISolanaAllocation,
  ISolanaCustomAllocation,
  ISolanaIndividualRights,
  ISolanaReservedRights,
  ITreasuryTokenAccInfo,
  RoleConfig,
} from "../../../common/interfaces/clubs.interface";
import {
  IDetailNftData,
  IDetailNftDataWithProposalInfo,
  IGetOrCreateTokenAccount,
  RoleDto,
} from "../../../common/interfaces/common.interface";
import {
  IDashboardDenominatedCurrencyWithTreasury,
  IDenominatedTokenInfo,
} from "../../../common/interfaces/user-dashboard.interface";
import { IAdminData } from "../../../common/interfaces/user.interface";
import {
  getDecimalsPerMint,
  getNftsFromRPC,
  getTreasuryCount,
  round,
} from "../../../utilities/helpers";
import { mapDepositRecords } from "../../methods/clubs";
import { getMintMetadata, parseMetadataToNft } from "../../methods/general";

import {
  acceptOfferSeed,
  escrowProgramFactory,
  escrowSeed,
  ESCRROW_PROGRAM,
  financialRecordSeed,
  governanceSeed,
  monkeAdminSeed,
  monkeAuthoritySeed,
  offerSeed,
  profitSeed,
  programFactory,
  RPC_CONNECTION,
  SOLANA_ENDPOINT,
  solanaApiLogo,
  splGovernanceProgram,
  stakeConfigSeed,
  stakedTokensSeed,
  stakeRecordSeed,
  tokenListApi,
  treasuryDataSeed,
  treasurySeed,
  unqClubSeed,
  vaultSeed,
} from "../utilities";
import { substractMagicEdenFees } from "./magic-eden.helpers";

export async function createTokenAccountOwnedBySpecifiedAccount(
  owner: PublicKey,
  newOwner: PublicKey,
  mint: PublicKey,
  amount?: number
) {
  const tokenAccount = new Keypair();
  const balanceNeeded = await Token.getMinBalanceRentForExemptAccount(
    RPC_CONNECTION
  );
  const createAccountIx = SystemProgram.createAccount({
    fromPubkey: owner,
    newAccountPubkey: tokenAccount.publicKey,
    lamports: amount ? amount : balanceNeeded,
    space: AccountLayout.span,
    programId: TOKEN_PROGRAM_ID,
  });

  const createInitAccountInstruction: TransactionInstruction =
    Token.createInitAccountInstruction(
      TOKEN_PROGRAM_ID,
      mint,
      tokenAccount.publicKey,
      newOwner
    );

  return {
    tokenAccount,
    createAccountIx,
    createInitAccountInstruction,
  };
}

export async function createATAOwnedBySpecifiedAccount(
  owner: AnchorWallet,
  mint: PublicKey
) {
  const ata = await Token.getAssociatedTokenAddress(
    ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
    TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
    mint, // mint
    owner.publicKey, // owner
    true
  );
  return Token.createAssociatedTokenAccountInstruction(
    ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
    TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
    mint, // mint
    ata, // ata
    owner.publicKey, // owner of token account
    owner.publicKey // fee payer
  );
}

export function arrayToRecord<T>(
  source: readonly T[],
  getKey: (item: T) => string
) {
  return source.reduce((all, a) => ({ ...all, [getKey(a)]: a }), {}) as Record<
    string,
    T
  >;
}
export const mapSellPermission = (
  sellPermissions: ISellPermissionConfigSolana[]
) => {
  const sellPermissionsConfig: ISellPermissionConfig[] = sellPermissions.map(
    (sellPermission) => {
      return {
        from: sellPermission.from.toNumber(),
        to: sellPermission.to.toNumber(),
        votingQuorum: sellPermission.quorumMinimum,
        governance: sellPermission.governance,
      };
    }
  );
  return sellPermissionsConfig;
};
export const getTotalFundraisedAmount = (fundraiseAccountsArray) => {
  return (
    fundraiseAccountsArray.reduce(
      (prev, next) => prev + next.raisedAmount * LAMPORTS_PER_SOL,
      0
    ) / LAMPORTS_PER_SOL
  );
};
export const checkIfRpcWorks = async (setError: (string) => void) => {
  try {
    await RPC_CONNECTION.getLatestBlockhash();
    setError(EMPTY_STRING);
    return true;
  } catch (error: any) {
    setError(RPC_ERROR);
    return false;
  }
};
export const checkIfSellPermissionIsValid = (
  sellPermission: ISellPermissionConfig
) => {
  return sellPermission.to !== 0;
};
export const getDepositAmountForMemberBasedOnSelectedFundraises = async (
  fundraiseCount: number,
  clubName: string,
  member: PublicKey,
  decimalsPerAmount: number,
  timestamp?: number
) => {
  const clubProgram = programFactory();

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [treasuryPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasuryPda.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [financialRecord] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      financialRecordSeed,
      member.toBuffer(),
    ],
    clubProgram.programId
  );

  try {
    const financialRecordAccount =
      await clubProgram.account.financialRecord.fetch(financialRecord);
    let accumulatedAmount = 0;

    for (let i = fundraiseCount; i > 0; i--) {
      const depositRecord = (
        financialRecordAccount.depositRecords as any[]
      )?.find((dep) => dep.fundraiseIndex === i);
      if (depositRecord) {
        accumulatedAmount = depositRecord.accumulatedAmount.toNumber();
        break;
      }
    }

    const depositRecords: IDepositRecord[] = [];

    for (const depositRecord of financialRecordAccount.depositRecords as any[]) {
      if (
        depositRecord.tradedRight &&
        //Think about this case when we implement multi fundraises
        depositRecord.fundraiseIndex === fundraiseCount
      ) {
        const boughtAmount = depositRecord.tradedRight
          .filter(
            (tr) =>
              Object.keys(tr.tradeType)[0] === TradedRightType.Buy ||
              Object.keys(tr.tradeType)[0] === TradedRightType.BuyReserved
          )
          .reduce((acc, trade) => {
            if (timestamp && trade.timestamp.toNumber() > timestamp) {
              return acc + 0;
            }
            return acc + trade.amount.toNumber();
          }, 0);
        const soldAmount = depositRecord.tradedRight
          .filter(
            (tr) =>
              Object.keys(tr.tradeType)[0] === TradedRightType.Sell ||
              Object.keys(tr.tradeType)[0] === TradedRightType.SellReserved
          )
          .reduce((acc, trade) => {
            if (timestamp && trade.timestamp.toNumber() > timestamp) {
              return acc + 0;
            }
            return acc + trade.amount.toNumber();
          }, 0);

        accumulatedAmount += boughtAmount - soldAmount;
      }
      const depositRecordData = mapDepositRecords(depositRecord);
      depositRecords.push(depositRecordData);
    }

    return {
      depositedAmount: accumulatedAmount / decimalsPerAmount,
      depositRecords,
    };
  } catch (error) {
    return { depositedAmount: 0, depositRecords: [] };
  }
};

//TODO@milica-monke: we dont have profit for now, but check for main instance
export async function getProfitForTreasury(treasury: PublicKey) {
  const clubProgram = programFactory();

  const [profit] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), profitSeed],
    clubProgram.programId
  );

  const rent = await RPC_CONNECTION.getMinimumBalanceForRentExemption(0);

  const profitWithRent = await RPC_CONNECTION.getBalance(profit);

  if (profitWithRent - rent === 0) {
    return 0;
  }
  return (profitWithRent - rent) / LAMPORTS_PER_SOL;
}

//TODO@guta: optimize this
//This function will be used when integrations are enabled
export async function getEscrowedNfts(
  realmName: string,
  communityMint: PublicKey
) {
  try {
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const allGovernances = await getAllGovernances(
      RPC_CONNECTION,
      splGovernanceProgram,
      realmAddress
    );

    const nftMintsOwnedByEscrow: {
      mint: PublicKey;
      tokenAddress: PublicKey;
      owner: PublicKey;
      proposalAddress: PublicKey;
    }[] = [];

    for (const governance of allGovernances) {
      for (
        let proposalCount = 0;
        proposalCount < governance.account.proposalCount;
        proposalCount++
      ) {
        try {
          const proposalIndexBuffer = Buffer.alloc(4);
          proposalIndexBuffer.writeInt32LE(proposalCount, 0);
          const [proposalAddress] = await PublicKey.findProgramAddress(
            [
              governanceSeed,
              governance.pubkey.toBuffer(),
              communityMint.toBuffer(),
              proposalIndexBuffer,
            ],
            splGovernanceProgram
          );
          const [offer] = await PublicKey.findProgramAddress(
            [offerSeed, proposalAddress.toBuffer()],
            ESCRROW_PROGRAM
          );
          const [escrowedWantedTokeAccount] =
            await PublicKey.findProgramAddress(
              [escrowSeed, offer.toBuffer(), acceptOfferSeed],
              ESCRROW_PROGRAM
            );
          const escrowedTokenAccounts =
            await RPC_CONNECTION.getParsedTokenAccountsByOwner(
              escrowedWantedTokeAccount,
              { programId: TOKEN_PROGRAM_ID }
            );
          const escrowedNfts = escrowedTokenAccounts.value.filter(
            (tokenAcc) =>
              tokenAcc.account.data.parsed.info.tokenAmount.decimals === 0 &&
              tokenAcc.account.data.parsed.info.tokenAmount.amount >= 1
          );
          escrowedNfts.forEach((escrowed) =>
            nftMintsOwnedByEscrow.push({
              mint: escrowed.account.data.parsed.info.mint,
              owner: governance.pubkey,
              tokenAddress: escrowed.pubkey,
              proposalAddress,
            })
          );
        } catch (error) {
          console.log("Error:", error);
        }
      }
    }
    return nftMintsOwnedByEscrow;
  } catch (error) {
    console.log(error);
    throw error;
  }
}
//This function will be used when integrations are enabled
export async function parseEscrowedNfts(
  realmName: string,
  communityMint: PublicKey
) {
  try {
    const basicNftsData = await getEscrowedNfts(realmName, communityMint);
    const nfts: IDetailNftData[] = [];
    for (const nftData of basicNftsData) {
      const mintMetadata = await getMintMetadata(nftData.mint);
      nfts.push(
        parseMetadataToNft(
          mintMetadata,
          nftData.mint,
          nftData.owner,
          nftData.tokenAddress,
          nftData.proposalAddress
        )
      );
    }
    return nfts;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 *
 * @param treasuryAddress Treasury public key
 * @param clubAddress Club data public key
 * @returns {IDetailNftData[]} All club nfts that are on sale(P2P or Magic Eden)
 */
export const getNftsOnSale = async (
  treasuryAddress: PublicKey,
  clubAddress: string
) => {
  try {
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const offers = await escrowProgram.account.offer.all();
    const nftsOnSale: IDetailNftDataWithProposalInfo[] = [];
    const peerToPeerBuyNfts: IDetailNftDataWithProposalInfo[] = [];

    const [treasuryPda] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        new PublicKey(clubAddress).toBuffer(),
        treasurySeed,
        getTreasuryCount(TREASURY_INDEX),
      ],
      clubProgram.programId
    );

    const clubOffers = offers.filter(
      (o) => o.account.treasury.toString() === treasuryPda.toString()
    );

    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        new PublicKey(clubAddress).toBuffer(),
        treasuryAddress.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    let hasNftSoldOnSolsea: boolean[] = [];

    for (const offer of clubOffers) {
      if (Object.keys(offer.account.status)[0] === OfferStatus.listedOnSolsea) {
        try {
          const nftSolseaData = await getNftDataFromSolsea(
            offer.account.offeredTokenMint.toString()
          );
          if (
            !nftSolseaData ||
            !nftSolseaData.listed ||
            (nftSolseaData.listed &&
              nftSolseaData.sellerKey !== offer.account.maker.toString())
          )
            hasNftSoldOnSolsea.push(true);
          else hasNftSoldOnSolsea.push(false);
        } catch (error) {
          hasNftSoldOnSolsea.push(true);
        }
      } else {
        hasNftSoldOnSolsea.push(false);
      }
    }

    const sellNftsOffers = clubOffers.filter(
      (offer, index) =>
        Object.keys(offer.account.status)[0] ===
          OfferStatus.executedSellOnMagicEden ||
        Object.keys(offer.account.status)[0] === OfferStatus.initialised ||
        (Object.keys(offer.account.status)[0] === OfferStatus.listedOnSolsea &&
          !hasNftSoldOnSolsea[index])
    );

    const peerToPeerBuy = clubOffers.filter(
      (offer) =>
        Object.keys(offer.account.status)[0] === OfferStatus.initialised &&
        offer.account.maker.toString() === treasuryPda.toString() &&
        offer.account.offerType[OfferTypeSolana.Buy]
    );

    const offeredTokenMints = sellNftsOffers.map(
      (offer) => offer.account.offeredTokenMint
    );

    const wantedTokenMints = peerToPeerBuy.map(
      (offer) => offer.account.wantedTokenMint
    );

    const instructionAddresses: PublicKey[] = [];
    const sellInstructionAddresses: PublicKey[] = [];

    for (const peerToPeerBuyProposal of peerToPeerBuy) {
      const [instructionAddress] = await PublicKey.findProgramAddress(
        [
          governanceSeed,
          peerToPeerBuyProposal.account.proposal.toBuffer(),
          optionIndexBuffer,
          instructionIndexBuffer,
        ],
        splGovernanceProgram
      );
      instructionAddresses.push(instructionAddress);
    }

    for (const sellProposal of sellNftsOffers) {
      const [instructionAddress] = await PublicKey.findProgramAddress(
        [
          governanceSeed,
          sellProposal.account.proposal.toBuffer(),
          optionIndexBuffer,
          instructionIndexBuffer,
        ],
        splGovernanceProgram
      );
      sellInstructionAddresses.push(instructionAddress);
    }

    const instucrionAddressesRawAccounts =
      await RPC_CONNECTION.getMultipleAccountsInfo(instructionAddresses);

    const rawSellInstructionAddresses =
      await RPC_CONNECTION.getMultipleAccountsInfo(sellInstructionAddresses);

    const makerOfferedData: {
      proposal: PublicKey;
      makerOfferedTa: PublicKey;
    }[] = [];

    const makerOfferedDataSell: {
      proposal: PublicKey;
      makerOfferedTa: PublicKey;
    }[] = [];

    for (const rawInstruction of instucrionAddressesRawAccounts) {
      if (!rawInstruction || !rawInstruction.data) continue;
      const deserializedTransaction: ProposalTransaction = deserializeBorsh(
        GOVERNANCE_SCHEMA,
        ProposalTransaction,
        rawInstruction.data
      );
      makerOfferedData.push({
        makerOfferedTa:
          deserializedTransaction.instructions[0].accounts[4].pubkey,
        proposal: deserializedTransaction.proposal,
      });
    }

    for (const rawInstruction of rawSellInstructionAddresses) {
      if (!rawInstruction || !rawInstruction.data) continue;
      const deserializedTransaction: ProposalTransaction = deserializeBorsh(
        GOVERNANCE_SCHEMA,
        ProposalTransaction,
        rawInstruction.data
      );
      makerOfferedDataSell.push({
        makerOfferedTa:
          deserializedTransaction.instructions[0].accounts[4].pubkey,
        proposal: deserializedTransaction.proposal,
      });
    }

    for (const [index, offeredMint] of offeredTokenMints.entries()) {
      try {
        const mintMetadata = await getMintMetadata(offeredMint);

        const [escrowedOfferedTokensAccountPda] =
          await PublicKey.findProgramAddress(
            [escrowSeed, sellNftsOffers[index].publicKey.toBuffer()],
            ESCRROW_PROGRAM
          );
        const relatedProposal = sellNftsOffers[index].account.proposal;

        let youWillGet = 0;
        const sellPrice = divide(
          sellNftsOffers[index].account.wantedAmount.toNumber(),
          LAMPORTS_PER_SOL
        );
        const status = Object.keys(sellNftsOffers[index].account.status)[0];

        if (
          Object.keys(sellNftsOffers[index].account.status)[0] ===
          OfferStatus.pendingSellExecutionOnMagicEden
        ) {
          const nftStats = await getNftDataFromMagicEden(
            offeredMint.toString()
          );
          youWillGet = substractMagicEdenFees(nftStats, sellPrice, status);

          if (
            !nftStats.listing[0] ||
            nftStats.listing[0].seller !== vaultAddress.toString()
          ) {
            continue;
          }
        } else {
          youWillGet = sellPrice;
        }

        nftsOnSale.push({
          escrowOfferedTokensAccount: escrowedOfferedTokensAccountPda,
          maker: vaultAddress,
          offer: sellNftsOffers[index].publicKey,
          ...parseMetadataToNft(mintMetadata, offeredMint),
          price: sellPrice,
          makerOfferedTokens: makerOfferedDataSell.find(
            (makerTa) =>
              makerTa.proposal.toString() === relatedProposal.toString()
          )!.makerOfferedTa,
          proposalPubkey: relatedProposal,
          status,
          youWillGet,
          isMagicEden: status === OfferStatus.executedSellOnMagicEden,
        });
      } catch (error) {
        console.log(error);
      }
    }

    for (const [index, wantedMint] of wantedTokenMints.entries()) {
      try {
        const mintMetadata = await getMintMetadata(wantedMint);

        const [escrowedOfferedTokensAccountPda] =
          await PublicKey.findProgramAddress(
            [escrowSeed, peerToPeerBuy[index].publicKey.toBuffer()],
            ESCRROW_PROGRAM
          );
        const relatedProposal = peerToPeerBuy[index].account.proposal;

        peerToPeerBuyNfts.push({
          escrowOfferedTokensAccount: escrowedOfferedTokensAccountPda,
          maker: treasuryPda,
          offer: peerToPeerBuy[index].publicKey,
          ...parseMetadataToNft(mintMetadata, wantedMint),
          price: divide(
            peerToPeerBuy[index].account.offeredAmount.toNumber(),
            LAMPORTS_PER_SOL
          ),
          makerOfferedTokens: makerOfferedData.find(
            (makerTa) =>
              makerTa.proposal.toString() === relatedProposal.toString()
          )!.makerOfferedTa,
          proposalPubkey: relatedProposal,
          status: Object.keys(peerToPeerBuy[index].account.status)[0],
          isMagicEden: false,
        });
      } catch (error) {
        console.log(error);
      }
    }

    return { nftsOnSale: nftsOnSale, buyPeerToPeer: peerToPeerBuyNfts };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

//This is helper function used to fetch staking data from program

export const fetchStakingsMainnetData = async () => {
  try {
    const clubProgram = programFactory();
    const stakingDatas: {
      name: string;
      clubName: string;
      depositAmount: number;
      lockedAt: Date | string;
      stakePeriodEnd: Date | string;
      stakedTokensAmount: number;
      numberOfDeposits;
    }[] = [];
    const allStakings = await clubProgram.account.stakeConfig.all();
    const hasReachedMax = allStakings.find(
      (st) => st.account.raisedAmount.toNumber() === 500000
    );

    const allClubs = await clubProgram.account.clubData.all();
    const allMemberDatas = await clubProgram.account.allowedMemberData.all();
    for (const staking of allStakings) {
      let relatedClubName: string;
      let stakedMembersNumber = 0;

      for (const club of allClubs) {
        for (let i = 0; i < club.account.stakeCount; i++) {
          const stakeBuff = Buffer.alloc(4);
          stakeBuff.writeInt32LE(i + 1, 0);
          const [stakeConfig] = await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              club.publicKey.toBuffer(),
              stakeConfigSeed,
              stakeBuff,
            ],
            clubProgram.programId
          );
          if (stakeConfig.toString() === staking.publicKey.toString()) {
            relatedClubName = club.account.name as string;
            const allClubMembers = allMemberDatas.filter(
              (acc) =>
                acc.account.clubData.toString() === club.publicKey.toString()
            );
            for (const clubMem of allClubMembers) {
              const [stakeRecordAddress] = await PublicKey.findProgramAddress(
                [
                  unqClubSeed,
                  clubMem.account.memberPubkey.toBuffer(),
                  stakeRecordSeed,
                  staking.publicKey.toBuffer(),
                ],
                clubProgram.programId
              );
              try {
                const stakedAm = await clubProgram.account.stakeRecord.fetch(
                  stakeRecordAddress
                );
                console.log(stakedAm, staking.account.name as string);

                stakedMembersNumber++;
              } catch (error) {}
            }
          }
        }
      }

      const depAmount =
        staking.account.raisedAmount.toNumber() / UNQ_PER_AMOUNT;
      const [stakedTokensAddress] = await PublicKey.findProgramAddress(
        [unqClubSeed, staking.publicKey.toBuffer(), stakedTokensSeed],
        clubProgram.programId
      );
      stakingDatas.push({
        clubName: relatedClubName!,
        name: staking.account.name as string,
        depositAmount: depAmount,

        lockedAt: dayjs
          .unix(staking.account.stakePeriodEnd!.toNumber())
          .isBefore(dayjs("2022-1-1"))
          ? "Staking not locked"
          : dayjs
              .unix(staking.account.stakePeriodEnd!.toNumber())
              .subtract(30, "days")
              .toDate(),
        stakePeriodEnd: dayjs
          .unix(staking.account.stakePeriodEnd!.toNumber())
          .isBefore(dayjs("2022-1-1"))
          ? "Staking not locked"
          : dayjs.unix(staking.account.stakePeriodEnd!.toNumber()).toDate(),
        stakedTokensAmount:
          +(await RPC_CONNECTION.getTokenAccountBalance(stakedTokensAddress))
            .value.amount / UNQ_PER_AMOUNT,

        numberOfDeposits: stakedMembersNumber,
      });
    }
    console.table(stakingDatas);
  } catch (error) {
    console.log(error);
  }
};

export const calculateCharacterBytes = (character: string) => {
  const bytes = Buffer.from(character);
  return bytes.byteLength;
};

export const getTreasuryAddressFroClub = async (clubAddress: string) => {
  const clubProgram = programFactory();
  const [treasuryPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      new PublicKey(clubAddress).toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );
  return treasuryPda;
};
export const parseDateToRelativeTime = (date: Date) => {
  const daysDiff = dayjs().diff(date, "days");
  if (daysDiff <= 0) {
    const mintutesDiff = dayjs().diff(date, "minutes");
    return `${
      mintutesDiff > 60
        ? `${(mintutesDiff / 60).toFixed(0)} hours ago`
        : `${mintutesDiff} minutes ago`
    }`;
  }
  if (daysDiff <= 14) {
    return `${daysDiff} days ago`;
  }
  const weeks = (daysDiff / 7).toFixed(0);
  return `${weeks} weeks ago`;
};

export const getSingleNftFromCollection = async (
  wallet: PublicKey,
  communityMint: PublicKey
) => {
  try {
    const nfts = await getNftsFromRPC(wallet, communityMint);
    let collectionName = EMPTY_STRING;
    try {
      collectionName = (
        await Metadata.findByMint(RPC_CONNECTION, communityMint)
      ).data.data.name;
    } catch (error) {}
    if (nfts.length > 0) {
      return { nft: nfts[0], collectionName };
    } else {
      //TODO@milica-monke-merge: For monke use SMB name, for main collectionName
      throw new Error(
        "You can not join the club because you do not have NFT from SMB collection"
      );
    }
  } catch (error) {
    throw error;
  }
};
const BASE_URL = process.env.REACT_APP_BASE_URL as string;
export const socket = io(BASE_URL);

export const mapErrorsFromApi = async (error: any) => {
  return await (await error.response).json();
};

export const getInfoForMintAccounts = async (addresses: PublicKey[]) => {
  try {
    if (addresses.length === 0) return [];

    const s = new Set();
    addresses.forEach((a) => s.add(a.toString()));

    const uniqueAddresses: string[] = Array.from(s) as string[];

    if (
      uniqueAddresses.length === 1 &&
      uniqueAddresses[0].toString() === USDC_ADDRESS
    ) {
      return [MISSING_TOKEN_DETAILS.get(USDC_ADDRESS)];
    }

    const data = await fetch(SOLANA_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getAssetBatch",
        params: {
          ids: uniqueAddresses,
        },
      }),
    });
    const parsedData = await data.json();

    const tokens = parsedData.result.map((p) => {
      return {
        address: p.id,
        logoURI: p.content.files[0].uri,
        symbol: p.content.metadata.symbol,
        decimals: p.token_info.decimals,
        name: p.content.metadata.name,
      }
    });
    return tokens.filter((t) => !!t);
  } catch (error) {
    return [MISSING_TOKEN_DETAILS.get(USDC_ADDRESS)];
  }
};

export const getTreasuyTokenAccountsInfo = async (
  treasury: PublicKey,
  treasuryBalance: number,
  profitAmount?: number,
  loadedProfitTokenAccounts?: RpcResponseAndContext<
    Array<{
      pubkey: PublicKey;
      account: AccountInfo<ParsedAccountData>;
    }>
  >
): Promise<ITreasuryTokenAccInfo[]> => {
  try {
    const clubProgram = programFactory();
    const treasuryTokensInfo: ITreasuryTokenAccInfo[] = [];
    const treasuryTokens = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
      treasury,
      {
        programId: TOKEN_PROGRAM_ID,
      }
    );
    let profitTokenAccounts = loadedProfitTokenAccounts;

    if (!profitTokenAccounts) {
      const [profit] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), profitSeed],
        clubProgram.programId
      );
      profitTokenAccounts = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
        profit,
        {
          programId: TOKEN_PROGRAM_ID,
        }
      );
    }

    const mints = treasuryTokens.value.map(
      (item) => new PublicKey(item.account.data.parsed.info.mint)
    );

    if (mints.length > 0) {
      const mintsWithInfo = await getInfoForMintAccounts(mints);

      // There are clubs with multiple token accounts for the same mint.
      for (const tokenAcc of treasuryTokens.value) {
        // // We have the option to show all of them, or remove the empty ones
        // if (tokenAcc.account.data.parsed.info.tokenAmount.uiAmount === 0) continue;
        const mintItem = mintsWithInfo.find(
          (item) => item.address === tokenAcc.account.data.parsed.info.mint
        );
        if (!mintItem) continue;
        const profitAcc = profitTokenAccounts.value.find(
          (item) => item.account.data.parsed.info.mint === mintItem.address
        );
        if (
          tokenAcc &&
          tokenAcc.account.data.parsed.info.mint !== NATIVE_MINT.toString()
        ) {
          let balance =
            tokenAcc.account.data.parsed.info.tokenAmount.uiAmount ?? 0;

          if (
            profitAcc &&
            profitAcc.account.data.parsed.info.tokenAmount.uiAmount
          ) {
            balance =
              balance + profitAcc.account.data.parsed.info.tokenAmount.uiAmount;
          }

          treasuryTokensInfo.push({
            balance: balance,
            image: mintItem.logoURI,
            mint: new PublicKey(mintItem.address),
            name: mintItem.name,
            tokenAddress: tokenAcc.pubkey,
            symbol: mintItem.symbol,
            decimals: mintItem.decimals,
            profit:
              profitAcc?.account.data.parsed.info.tokenAmount.uiAmount ?? 0,
          });
        }
      }
    }
    treasuryTokensInfo.push({
      balance: profitAmount ? treasuryBalance + profitAmount : treasuryBalance,
      image: solanaApiLogo,
      mint: NATIVE_MINT,
      name: "Solana",
      tokenAddress: treasuryTokens.value.find(
        (item) =>
          item.account.data.parsed.info.mint.toString() ===
          NATIVE_MINT.toString()
      )?.pubkey,
      symbol: "SOL",
      decimals: 9,
      profit: profitAmount ?? 0,
    });
    return treasuryTokensInfo;
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getDenominatedTokenInfo = async (
  financialTokenAccount: PublicKey,
  denominatedCurrency: PublicKey,
  profitTokenAccount:
    | {
        pubkey: PublicKey;
        account: AccountInfo<ParsedAccountData>;
      }
    | undefined
): Promise<ITreasuryTokenAccInfo> => {
  try {
    const tokenBalance = await RPC_CONNECTION.getTokenAccountBalance(
      financialTokenAccount
    );
    const mintTokenInfo = await getInfoForMintAccounts([denominatedCurrency]);
    let balance = tokenBalance.value.uiAmount ?? 0;

    if (
      profitTokenAccount &&
      profitTokenAccount.account.data.parsed.info.tokenAmount.uiAmount
    ) {
      balance =
        balance +
        profitTokenAccount.account.data.parsed.info.tokenAmount.uiAmount;
    }

    return {
      balance: balance,
      decimals: mintTokenInfo[0].decimals,
      image: mintTokenInfo[0].logoURI,
      mint: denominatedCurrency,
      name: mintTokenInfo[0].name,
      symbol: mintTokenInfo[0].symbol,
      tokenAddress: financialTokenAccount,
      profit:
        profitTokenAccount?.account.data.parsed.info.tokenAmount.uiAmount ?? 0,
    };
  } catch (error) {
    throw error;
  }
};

export const getMultipleDenominatedTokensInfo = async (
  allDenominatedCurrencies: IDenominatedTokenInfo[]
): Promise<IDashboardDenominatedCurrencyWithTreasury[]> => {
  try {
    const tokenAccountsInfo: IDashboardDenominatedCurrencyWithTreasury[] = [];
    const multupleTokensInfo = await RPC_CONNECTION.getMultipleAccountsInfo(
      allDenominatedCurrencies.map((item) => item.financialTokenAccount)
    );

    const mintTokensInfo = await getInfoForMintAccounts(
      allDenominatedCurrencies.map((item) => item.denominatedCurrency)
    );

    for (const [index, token] of multupleTokensInfo.entries()) {
      if (!token) continue;
      const data = Buffer.from(token.data);
      const accountInfo = AccountLayout.decode(data);
      accountInfo.mint = new PublicKey(accountInfo.mint);
      accountInfo.amount = u64.fromBuffer(accountInfo.amount);
      const mintInfo = mintTokensInfo.find(
        (item) => item.address === accountInfo.mint.toString()
      );

      if (mintInfo) {
        //Keep in mind that profit and balance+profit are calculated after function call
        tokenAccountsInfo.push({
          tokenInfo: {
            balance: divide(
              Number(accountInfo.amount),
              Number(pow(10, mintInfo.decimals))
            ),
            decimals: mintInfo.decimals,
            image: mintInfo.logoURI,
            mint: accountInfo.mint,
            name: mintInfo.name,
            symbol: mintInfo.symbol,
            tokenAddress: accountInfo.address,
            profit: 0,
          },
          treasury: allDenominatedCurrencies[index].treasuryAddress,
        });
      }
    }
    return tokenAccountsInfo;
  } catch (error) {
    throw error;
  }
};

export const getDefaultMemberPermissions = () => {
  return [
    ClubAction.CastVote,
    ClubAction.Distribute,
    ClubAction.SupportClub,
    ClubAction.StakeTokens,
    ClubAction.CreateFinancialOffer,
    ClubAction.AcceptFinancialOffer,
    ClubAction.CanLeave,
  ];
};

export const getDefaultFounderPermissions = () => {
  return [
    ClubAction.CastVote,
    ClubAction.CreateDiscussionProposal,
    ClubAction.CreateTreasuryGovernance,
    ClubAction.CreateWithdrawalGovernance,
    ClubAction.CreateTransferGovernance,
    ClubAction.CreateP2PProposal,
    ClubAction.CreateWithdrawalProposal,
    ClubAction.CreateTransferProposal,
    ClubAction.CreateMeProposal,
    ClubAction.SignOffProposal,
    ClubAction.CancelProposal,
    ClubAction.AllowMember,
    ClubAction.CancelP2POffer,
    ClubAction.CancelInvitation,
    ClubAction.Fundraise,
    ClubAction.AddReservedRights,
    ClubAction.Distribute,
    ClubAction.SupportClub,
    ClubAction.UpdateMember,
    ClubAction.InitializeStaking,
    ClubAction.StakeTokens,
    ClubAction.CreateFinancialOffer,
    ClubAction.AcceptFinancialOffer,
    ClubAction.CreateSolseaProposal,
    ClubAction.CreateChangeClubConfigGovernance,
    ClubAction.UpdateGovernanceConfig,
    ClubAction.UpdateRoleConfig,
    ClubAction.AddReservedRights,
    ClubAction.AddReservedRights,
    ClubAction.UpdateAllocation,
    //TODO@milica-monke-merge: we do not have this in monke, only for main app
    ClubAction.AddReservedRightsToSelf,
  ];
};

export const getDefaultManagerPermissions = () => {
  return [
    ClubAction.CastVote,
    ClubAction.CreateDiscussionProposal,
    ClubAction.CreateP2PProposal,
    ClubAction.CreateWithdrawalProposal,
    ClubAction.CreateTransferProposal,
    ClubAction.CreateMeProposal,
    ClubAction.SignOffProposal,
    ClubAction.CancelProposal,
    ClubAction.Distribute,
    ClubAction.SupportClub,
    ClubAction.StakeTokens,
    ClubAction.CreateFinancialOffer,
    ClubAction.AcceptFinancialOffer,
    ClubAction.CreateSolseaProposal,
    ClubAction.UpdateGovernanceConfig,
    ClubAction.UpdateRoleConfig,
    ClubAction.CanLeave,
  ];
};

export const getProjectFundraiseInvestorPermissions = () => {
  return [
    ClubAction.CastVote,
    ClubAction.CreateWithdrawalProposal,
    ClubAction.SignOffProposal,
    ClubAction.Distribute,
    ClubAction.SupportClub,
    ClubAction.StakeTokens,
    ClubAction.CreateFinancialOffer,
    ClubAction.AcceptFinancialOffer,
    ClubAction.CanLeave,
  ];
};

export const getProjectFundraiseFounderPermissions = () => {
  return [
    ClubAction.CreateDiscussionProposal,
    ClubAction.CreateTreasuryGovernance,
    ClubAction.CreateWithdrawalGovernance,
    ClubAction.CreateTransferGovernance,
    ClubAction.CreateTransferProposal,
    ClubAction.SignOffProposal,
    ClubAction.AllowMember,
    ClubAction.CancelInvitation,
    ClubAction.Fundraise,
    ClubAction.AddReservedRights,
    ClubAction.Distribute,
    ClubAction.UpdateMember,
    ClubAction.InitializeStaking,
    ClubAction.StakeTokens,
    ClubAction.UpdateAllocation,
  ];
};

export const getRoleConfiguration = (values: ICreateClubWizardForm) => {
  const roleConfig: RoleDto[] = [];
  switch (values.governance) {
    case GovernanceConfigurationType.ExistingToken: {
      roleConfig.push(
        new RoleDto(
          UserRole.owner,
          new BN(0),
          getDefaultFounderPermissions(),
          0
        )
      );
      roleConfig.push(
        new RoleDto(
          UserRole.member,
          new BN(0),
          getDefaultMemberPermissions(),
          0
        )
      );
      values.curator &&
        roleConfig.push(
          new RoleDto(
            UserRole.curator,
            new BN(0),
            getDefaultManagerPermissions(),
            0
          )
        );
      return roleConfig;
    }
    case GovernanceConfigurationType.NFT: {
      roleConfig.push(
        new RoleDto(
          UserRole.owner,
          new BN(0),
          getDefaultFounderPermissions(),
          0
        )
      );
      roleConfig.push(
        new RoleDto(
          UserRole.member,
          new BN(0),
          getDefaultMemberPermissions(),
          0
        )
      );
      values.curator &&
        roleConfig.push(
          new RoleDto(
            UserRole.curator,
            new BN(0),
            getDefaultManagerPermissions(),
            0
          )
        );
      return roleConfig;
    }
    case GovernanceConfigurationType.RoleBased: {
      roleConfig.push(
        new RoleDto(
          UserRole.owner,
          new BN(values.ownerVotingPower * LAMPORTS_PER_SOL),
          getDefaultFounderPermissions(),
          0
        )
      );
      roleConfig.push(
        new RoleDto(
          UserRole.member,
          new BN(values.memberVotingPower * LAMPORTS_PER_SOL),
          getDefaultMemberPermissions(),
          0
        )
      );
      values.curator &&
        roleConfig.push(
          new RoleDto(
            UserRole.curator,
            new BN(values.curatorVotingPower * LAMPORTS_PER_SOL),
            getDefaultManagerPermissions(),
            0
          )
        );
      return roleConfig;
    }
    case GovernanceConfigurationType.ProjectFundraise: {
      roleConfig.push(
        new RoleDto(
          UserRole.owner,
          new BN(0),
          getProjectFundraiseFounderPermissions(),
          0
        )
      );
      roleConfig.push(
        new RoleDto(
          UserRole.investor,
          new BN(0),
          getProjectFundraiseInvestorPermissions(),
          0
        )
      );
      return roleConfig;
    }
  }
};

export const mapClubActionFromSolanaToClubAction = (
  clubAction: ClubActionSolana
) => {
  switch (clubAction) {
    case ClubActionSolana.AllowMember:
      return ClubAction.AllowMember;
    case ClubActionSolana.CancelInvitation:
      return ClubAction.CancelInvitation;
    case ClubActionSolana.CancelP2POffer:
      return ClubAction.CancelP2POffer;
    case ClubActionSolana.CancelProposal:
      return ClubAction.CancelProposal;
    case ClubActionSolana.CastVote:
      return ClubAction.CastVote;
    case ClubActionSolana.CreateDiscussionProposal:
      return ClubAction.CreateDiscussionProposal;
    case ClubActionSolana.CreateMeProposal:
      return ClubAction.CreateMeProposal;
    case ClubActionSolana.CreateP2PProposal:
      return ClubAction.CreateP2PProposal;
    case ClubActionSolana.CreateTransferGovernance:
      return ClubAction.CreateTransferGovernance;
    case ClubActionSolana.CreateTreasuryGovernance:
      return ClubAction.CreateTreasuryGovernance;
    case ClubActionSolana.CreateTransferProposal:
      return ClubAction.CreateTransferProposal;
    case ClubActionSolana.CreateWithdrawalGovernance:
      return ClubAction.CreateWithdrawalGovernance;
    case ClubActionSolana.CreateWithdrawalProposal:
      return ClubAction.CreateWithdrawalProposal;
    case ClubActionSolana.Distribute:
      return ClubAction.Distribute;
    case ClubActionSolana.Fundraise:
      return ClubAction.Fundraise;
    case ClubActionSolana.InitializeStaking:
      return ClubAction.InitializeStaking;
    case ClubActionSolana.SignOffProposal:
      return ClubAction.SignOffProposal;
    case ClubActionSolana.StakeTokens:
      return ClubAction.StakeTokens;
    case ClubActionSolana.SupportClub:
      return ClubAction.SupportClub;
    case ClubActionSolana.UpdateMember:
      return ClubAction.UpdateMember;
    case ClubActionSolana.CreateFinancialOffer:
      return ClubAction.CreateFinancialOffer;
    case ClubActionSolana.AcceptFinancialOffer:
      return ClubAction.AcceptFinancialOffer;
    case ClubActionSolana.UpdateGovernanceConfig:
      return ClubAction.UpdateGovernanceConfig;
    case ClubActionSolana.UpdateRoleConfig:
      return ClubAction.UpdateRoleConfig;
  }
};

//TODO@milica-roles: set userRole to optional param
export const checkIfUserHasPermissionForAction = (
  userRole: string,
  roleConfig: RoleConfig[],
  clubAction: ClubAction
) => {
  return !!roleConfig
    .find((item) => item.name === userRole)
    ?.actions.some((item) => item === clubAction);
};

export const checkIfUserCanCreateAtLeastOneTypeOfProposal = (
  userRole: string,
  roleConfig: RoleConfig[]
) => {
  return !!roleConfig
    .find((item) => item.name === userRole)
    ?.actions.some(
      (item) =>
        item === ClubAction.CreateMeProposal ||
        item === ClubAction.CreateP2PProposal ||
        item === ClubAction.CreateDiscussionProposal ||
        item === ClubAction.CreateTransferProposal ||
        item === ClubAction.CreateWithdrawalProposal ||
        item === ClubAction.CreateChangeClubConfigGovernance
    );
};

export const checkIfUserHasPermissionToCreateTradingProposal = (
  userRole: string,
  roleConfig: RoleConfig[]
) => {
  return (
    !!roleConfig
      .find((item) => item.name === userRole)
      ?.actions.some((item) => item === ClubAction.CreateP2PProposal) &&
    !!roleConfig
      .find((item) => item.name === userRole)
      ?.actions.some((item) => item === ClubAction.CreateMeProposal)
  );
};

export const getPercentageRights = (
  amountsOfShares: number,
  clubTreasury: IClubTreasury
) => {
  return round(
    (amountsOfShares /
      clubTreasury.totalFinancialPower /
      getDecimalsPerMint(clubTreasury?.denominatedTokenInfo)) *
      100,
    2
  );
};
//todo replace similar logic with this function
export const getOrCreateTokenAccount = async (
  wantedMint: PublicKey,
  address: PublicKey,
  wallet: AnchorWallet
): Promise<IGetOrCreateTokenAccount> => {
  let destination: PublicKey | undefined;
  const destinationTA = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
    address,
    {
      mint: wantedMint,
    }
  );

  if (destinationTA.value.length) {
    destination = destinationTA.value[0].pubkey;
  } else {
    destination = await Token.getAssociatedTokenAddress(
      ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
      TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
      wantedMint, // mint
      address, // owner
      false
    );
    const instruction = await Token.createAssociatedTokenAccountInstruction(
      ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
      TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
      wantedMint, // mint
      destination, // ata
      address, // owner of token account
      wallet.publicKey // fee payer
    );
    return { destination, instruction };
  }
  return { destination };
};

export const canMemberVoteOnProposal = (
  memberRights: IMembersRights,
  proposalCreationTimestamp: number
): boolean => {
  try {
    if (
      (memberRights.depositedAmount && memberRights.depositedAmount > 0) ||
      (memberRights.votingPower && memberRights.votingPower > 0)
    )
      return true;
    let accumulatedAmountBeforeTimestamp = 0;
    memberRights.depositRecords.forEach((depRecord) => {
      if (depRecord.tradedRight) {
        accumulatedAmountBeforeTimestamp += depRecord.tradedRight.reduce(
          (acc, val) => {
            if (val.timestamp > proposalCreationTimestamp) return acc + 0;
            if (val.tradedType === TradedRightType.Sell)
              return acc - val.amount;
            else return acc + val.amount;
          },
          0
        );
      }
    });
    return accumulatedAmountBeforeTimestamp > 0;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const writeValueToNewInt32Buffer = (value: number) => {
  const bufferValue = Buffer.alloc(4);
  bufferValue.writeInt32LE(value, 0);
  return bufferValue;
};

export const mapAllocation = (
  values: ICreateFundraiseForm,
  denominatedTokenInfo?: ITreasuryTokenAccInfo
): ISolanaAllocation | null => {
  const customAllocation: ISolanaCustomAllocation[] = [];
  let equal: BN | null = null;
  values.customAllocation.forEach((value) => {
    if (value.isAssigned)
      Object.keys(value.selectedMembers).forEach((key) => {
        customAllocation.push({
          amount: new BN(
            value.amount * getDecimalsPerMint(denominatedTokenInfo)
          ),
          authority: new PublicKey(key),
        });
      });
  });

  if (values.memberLimitedAmount) {
    equal = new BN(
      values.memberLimitedAmount * getDecimalsPerMint(denominatedTokenInfo)
    );
  }

  if (customAllocation.length === 0 && !equal) {
    return null;
  }

  return {
    custom: customAllocation.length > 0 ? customAllocation : null,
    equal,
  };
};
export const mapReservedRightsForCreateFundraise = (
  values: ICreateFundraiseForm
): ISolanaReservedRights | undefined => {
  const individualRights: ISolanaIndividualRights[] = [];
  let reservedPercentage = 0;
  values.clubOwnership.forEach((value) => {
    Object.keys(value.selectedMembers).forEach((key) => {
      reservedPercentage = reservedPercentage + value.percentageAmount * 100;
      individualRights.push({
        memberPubkey: new PublicKey(key),
        rightPercentage: value.percentageAmount * 100,
        amountOfRights: new BN(0),
      });
    });
  });

  return individualRights.length > 0
    ? {
        totalReservedRights: new BN(0),
        totalReservedPercentage: reservedPercentage,
        individualRights: individualRights,
      }
    : undefined;
};

/**
 *
 * @param memberPubkey
 * @param denominatedTokenInfo
 * @param lastFundraise
 * @param lastDepositRecord
 * @returns {number | undefined}
 */

export const getMemberAllocation = (
  memberPubkey: PublicKey,
  denominatedTokenInfo?: ITreasuryTokenAccInfo,
  lastFundraise?: IFundraise,
  lastDepositRecord?: IDepositRecord
): number | undefined => {
  if (lastFundraise?.allocation?.custom) {
    const memberAllocation = lastFundraise.allocation.custom.find(
      (member) => member.authority.toString() === memberPubkey.toString()
    )?.amount;
    if (memberAllocation)
      return lastDepositRecord
        ? memberAllocation -
            lastDepositRecord.accumulatedAmount /
              getDecimalsPerMint(denominatedTokenInfo)
        : memberAllocation;
  }
  return lastDepositRecord && lastFundraise?.allocation?.equal
    ? lastFundraise?.allocation?.equal -
        lastDepositRecord.accumulatedAmount /
          getDecimalsPerMint(denominatedTokenInfo)
    : lastFundraise?.allocation?.equal ?? undefined;
};

export const getMemberReservedRights = (
  memberPubkey: PublicKey,
  reservedRights?: IReservedRights
) => {
  return reservedRights?.individualRights.find(
    (item) => item.memberPubkey.toString() === memberPubkey.toString()
  );
};

export const parseReserverRightsFromProgramToTreasuryInfo = (
  reservedRights,
  individualRights: ISolanaIndividualRights[],
  decimalsPerMint: number
): IReservedRights => {
  return {
    totalReservedRights:
      reservedRights.totalReservedRights.toNumber() / decimalsPerMint,
    totalReservedPercentage: reservedRights.totalReservedPercentage / 100,
    individualRights: individualRights.map((item) => {
      return {
        memberPubkey: item.memberPubkey,
        rightPercentage: item.rightPercentage / 100,
        amountOfRights: item.amountOfRights.toNumber() / decimalsPerMint,
      };
    }),
  };
};

export const getAccumulatedAmountForDepositRecord = (
  depositRecords: IDepositRecord[],
  fundraiseCount: number,
  timestamp: number
) => {
  let accumulatedAmount = 0;
  for (let i = fundraiseCount; i > 0; i--) {
    const depositRecord = depositRecords?.find(
      (dep) => dep.fundraiseIndex === i
    );
    if (depositRecord) {
      accumulatedAmount = depositRecord.accumulatedAmount;
      break;
    }
  }
  for (const depositRecord of depositRecords) {
    if (
      depositRecord.tradedRight &&
      depositRecord.fundraiseIndex === fundraiseCount
    ) {
      const boughtAmount = depositRecord.tradedRight
        .filter(
          (tr) =>
            tr.tradedType === TradedRightType.Buy ||
            tr.tradedType === TradedRightType.BuyReserved
        )
        .reduce((acc, trade) => {
          if (timestamp && trade.timestamp > timestamp) {
            return acc + 0;
          }
          return acc + trade.amount;
        }, 0);
      const soldAmount = depositRecord.tradedRight
        .filter(
          (tr) =>
            tr.tradedType === TradedRightType.Sell ||
            tr.tradedType === TradedRightType.SellReserved
        )
        .reduce((acc, trade) => {
          if (timestamp && trade.timestamp > timestamp) {
            return acc + 0;
          }
          return acc + trade.amount;
        }, 0);

      accumulatedAmount += boughtAmount - soldAmount;
    }
  }
  return accumulatedAmount;
};

export const getBuyOrSellSharesModalHeaderCopy = (
  clubBasicInfo: IClubBasicInfo
) => {
  return clubBasicInfo.kycConfig
    ? `In order to be able to perform trade of Financial rights both seller and buyer need to be members of the same Club and have their identities verified.`
    : `In order to be able to perform trade of Financial rights both seller and buyer need to be members of the same Club.`;
};
//TODO@milica-monke-merge: Only for monke
export const checkIfUserCanCreateClub = (
  wallet: PublicKey | null,
  adminData: IAdminData | undefined
) => {
  if (!wallet) return false;
  if (adminData) {
    return (
      !!adminData.permissions.find(
        (p) => p === MonkeAdminPermissions.CreateClub
      ) && adminData.status === MonkeAdminStatus.Accepted
    );
  } else {
    return false;
  }
};

export const getWhitelistedUsers = async () => {
  const clubProgram = programFactory();
  try {
    const [whitelistingData] = await PublicKey.findProgramAddress(
      [unqClubSeed, monkeAuthoritySeed],
      clubProgram.programId
    );

    const whitelistings = await clubProgram.account.whitelistingData.fetch(
      whitelistingData
    );

    return whitelistings.whitelistedMembers;
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getAdminData = async (
  wallet: WalletContextState
): Promise<IAdminData | undefined> => {
  const clubProgram = programFactory();
  try {
    const [adminData] = await PublicKey.findProgramAddress(
      [unqClubSeed, monkeAdminSeed],
      clubProgram.programId
    );
    const adminDataAccount = await clubProgram.account.monkeAdmins.fetch(
      adminData
    );
    const admin = (adminDataAccount.adminConfigs as any[]).find(
      (adminConfig) =>
        adminConfig.admin.toString() === wallet!.publicKey!.toString()
    );

    if (admin) {
      return {
        status: Object.keys(admin.status)[0] as MonkeAdminStatus,
        user: admin.admin,
        permissions: admin.permissions.map(
          (perm) => Object.keys(perm)[0] as MonkeAdminPermissions
        ),
      };
    }
    return admin;
  } catch (error) {
    console.log(error);
  }
};

export async function transferToSelf(
  wallet: WalletContextState
): Promise<string> {
  let transferTransaction = new Transaction({
    recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    feePayer: wallet.publicKey,
  }).add(
    SystemProgram.transfer({
      fromPubkey: wallet.publicKey!,
      toPubkey: wallet.publicKey!,
      lamports: 0,
    })
  );
  transferTransaction = await wallet!.signTransaction!(transferTransaction);
  return await sendAndConfirmRawTransaction(
    RPC_CONNECTION,
    transferTransaction.serialize(),
    { preflightCommitment: "confirmed" }
  );
}

export const convertNestedObjectToArray = (nestedObj) =>
  Object.keys(nestedObj).map((key) => nestedObj[key]);

export const extractNumbersFromString = (str: string): string =>
  str.replace(/[^(\d+)\.(\d+)]/g, "");
